import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { SPATIAL_LAYER_INTERSECT } from '@rsos/constants/dataTypeSuffixes';
import { parseWSMessage } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { getCallQueueMaxAge } from '@rsos/utils/metaTags';
import {
  setStaticSpatialObjectActiveStatus,
  setStaticSpatialObjectInactiveStatus,
} from '../../../actions/spatialLayers';

/**
 * Hook that gets invoked as a part of StreamWS callbacks to intercept messages
 * that instruct us to change spatial polygon color when a call intersects the
 * premises. We parse the message type and watch for the
 * `spatial_layer_intersect` suffix. If one is detected we fire an instruction
 * to make the premises status active and turn the polygon red.
 */
const useSpatialLayerIntersect = () => {
  const StreamWS = window.__RSOS?.streamWS?.instance;

  const dispatch = useDispatch();

  const pendingReversions = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set(),
  );

  const handleSpatialLayerIntersect = useCallback(
    (message: Record<string, unknown>) => {
      const parsedMessageType = parseWSMessage(message);
      if (parsedMessageType?.suffix === SPATIAL_LAYER_INTERSECT) {
        dispatch(setStaticSpatialObjectActiveStatus({ data: message?.body }));
        const timeoutId = setTimeout(
          () => {
            pendingReversions.current.delete(timeoutId);
            dispatch(
              setStaticSpatialObjectInactiveStatus({ data: message?.body }),
            );
          },
          Number(getCallQueueMaxAge()) * 1000,
        );
        pendingReversions.current.add(timeoutId);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const pending = pendingReversions.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleSpatialLayerIntersect: handleSpatialLayerIntersect,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleSpatialLayerIntersect');
      };
    }
  }, [StreamWS, handleSpatialLayerIntersect]);

  return null;
};

export default useSpatialLayerIntersect;
